import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  TextField,
} from "@mui/material";
import { CloseIconFill } from "../../../ImageRepo";
import ShepButton from "../ShepButton";
import "./ShepDialog.css";

/*
 * ShepAlertDialog - Replacement for prompt() that just displays a message with a text or numeric input and Submit/Cancel buttons
*/

const ShepPromptDialog = ({
  open,
  onClose,
  onSubmit,
  title = "Input Required",
  message,
  defaultValue = "",
  placeholder = "",
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  required = false,
  multiline = false,
  rows = 1,
  type = "text", // "text" | "number"
  min,
  max,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const inputRef = useRef(null);

  // Reset input when dialog opens
  useEffect(() => {
    if (open) {
      setInputValue(defaultValue);
      // Auto-focus after a short delay to ensure dialog is rendered
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, defaultValue]);

  const handleSubmit = () => {
    if (required && !inputValue.toString().trim()) {
      return;
    }
    onSubmit?.(inputValue);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "9px",
          overflow: "hidden",
        },
      }}
      sx={{ zIndex: 1900 }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          px: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <IconButton
          onClick={handleCancel}
          sx={{ color: "var(--default-light)", p: 0.5 }}
          aria-label="Close dialog"
        >
          <CloseIconFill style={{ width: 14, height: 14 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          pb: 2,
          px: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "120px",
        }}
      >
        {message && (
          <Typography
            variant="body1"
            sx={{
              color: "var(--text-primary)",
              whiteSpace: "pre-line",
              fontSize: "var(--font-sm)",
              textAlign: "center",
              mb: 2,
            }}
          >
            {message}
          </Typography>
        )}
        <TextField
          inputRef={inputRef}
          fullWidth
          value={inputValue}
          onChange={(e) =>
            setInputValue(e.target.value)
          }
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          multiline={multiline}
          rows={multiline ? rows : 1}
          required={required}
          type={type}
          inputProps={{
            min: min,
            max: max,
          }}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              fontSize: "var(--font-sm)",
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 2, justifyContent: "center" }}>
        <ShepButton variant="secondary" size="small" onClick={handleCancel}>
          {cancelLabel}
        </ShepButton>
        <ShepButton
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={required && !inputValue.toString().trim()}
        >
          {submitLabel}
        </ShepButton>
      </DialogActions>
    </Dialog>
  );
};

ShepPromptDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  submitLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  required: PropTypes.bool,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  type: PropTypes.oneOf(["text", "number", "email"]),
  min: PropTypes.number,
  max: PropTypes.number,
};

export default ShepPromptDialog;
